import React, {useEffect, useRef, useState} from 'react';
import {InformationCircleIcon} from '@heroicons/react/24/outline';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Link, useNavigate} from 'react-router-dom';
import Button from '@/components/ui/Button';
import ConfirmModal from '@/components/ui/ConfirmModal';
import LoadingStatus from '@/components/ui/LoadingStatus';
import {PlayerDetail, playersV2API, SquadSyncResult} from '@/features/players/api';
import {playerKeys} from '@/features/players/queries';
import {
  WyscoutImportMonths,
  WyscoutPlayerSearchResult,
  routeablePlayerConflict,
  wyscoutImportAPI,
} from '@/features/players/wyscoutImportApi';
import {Capability, getV2Error} from '@/services/api/v2';
import {WyscoutClubRosterImportResult} from '@/features/players/wyscoutClubRosterApi';
import WyscoutClubRosterDialog from './WyscoutClubRosterDialog';
import WyscoutImportPanel from './WyscoutImportPanel';

interface Props {
  workspaceId: string;
  player: PlayerDetail;
  capabilities: Capability[];
  selectedMatchId?: number | null;
  onOpenFixture?: (matchId: number, trigger: HTMLButtonElement) => void;
}

type ImportDialog =
  | {kind: 'linked'; months: WyscoutImportMonths}
  | {kind: 'link'; player: WyscoutPlayerSearchResult; months: WyscoutImportMonths}
  | null;

interface ImportVariables {
  dialog: Exclude<ImportDialog, null>;
  workspaceId: string;
  playerId: string;
  token: object;
}

interface SyncVariables {
  workspaceId: string;
  playerId: string;
  token: object;
}

const competitionNames: Record<number, string> = {
  350: 'National League North/South',
  351: 'National League',
  359: 'League Two',
  361: 'Non League Premier',
};

const formatBirthDate = (value: string): string => {
  if (!value) return 'Not provided';
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return 'Not provided';
  return new Intl.DateTimeFormat('en-GB', {day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'}).format(date);
};

const syncResultLabel = (result: SquadSyncResult): string =>
  `${result.added} added, ${result.updated} updated, ${result.reactivated} reactivated, ${result.review} for review, ${result.ignored} ignored, ${result.unchanged} unchanged.`;

const PeriodSelect: React.FC<{
  value: WyscoutImportMonths;
  disabled?: boolean;
  onChange: (months: WyscoutImportMonths) => void;
}> = ({value, disabled, onChange}) => (
  <label className="block text-left font-semibold text-gray-800 dark:text-gray-200">
    History to import
    <select
      value={value}
      disabled={disabled}
      onChange={event => onChange(Number(event.target.value) as WyscoutImportMonths)}
      className="mt-1 block min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
    >
      <option value={12}>12 months</option>
      <option value={24}>24 months</option>
      <option value={36}>36 months</option>
    </select>
  </label>
);

const SearchResult: React.FC<{
  player: WyscoutPlayerSearchResult;
  currentPlayerId: string;
  workspaceId: string;
  onSelect: () => void;
}> = ({player, currentPlayerId, workspaceId, onSelect}) => {
  const navigate = useNavigate();
  const lookupPending = React.useRef(false);
  const conflict = Boolean(player.existingPlayerId && player.existingPlayerId !== currentPlayerId);
  const existingPlayer = useMutation({
    mutationFn: (playerId: string) => playersV2API.detail<PlayerDetail>(playerId, workspaceId),
    onSuccess: ({data}, playerId) => navigate(data.scope === 'squad' ? `/players/${playerId}` : `/scouting/${playerId}`),
    onSettled: () => { lookupPending.current = false; },
  });
  return (
    <article aria-label={player.name} className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex min-w-0 items-start gap-3">
        {player.imageUrl
          ? <img src={player.imageUrl} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
          : <div aria-hidden="true" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-club-navy-950 text-lg font-bold text-white">{player.name.trim().charAt(0).toUpperCase() || '?'}</div>}
        <div className="min-w-0">
          <h3 className="break-words font-bold text-gray-900 dark:text-gray-100">{player.name}</h3>
          <p className="break-words text-sm text-gray-700 dark:text-gray-300">{[player.position, player.teamName].filter(Boolean).join(' | ') || 'Profile details unavailable'}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Born {formatBirthDate(player.birthDate)}</p>
          {player.nationality && <p className="text-sm text-gray-600 dark:text-gray-400">{player.nationality}</p>}
        </div>
      </div>
      {player.competitionIds.length > 0 && <ul aria-label={`${player.name} competitions`} className="mt-3 flex flex-wrap gap-2">
        {player.competitionIds.map(id => <li key={id} className="rounded-full bg-club-blue-50 px-2.5 py-1 text-xs font-semibold text-club-blue-800 dark:bg-club-blue-950 dark:text-club-blue-200">{competitionNames[id] ?? `Competition ${id}`}</li>)}
      </ul>}
      {conflict
        ? <div className="mt-3 space-y-2">
          <p className="text-sm text-amber-800 dark:text-amber-200">This WyScout identity is already linked to another player.</p>
          <Button
            variant="outline"
            className="min-h-11"
            loading={existingPlayer.isPending}
            loadingLabel="Opening existing player"
            onClick={() => {
              if (!player.existingPlayerId || lookupPending.current) return;
              lookupPending.current = true;
              existingPlayer.mutate(player.existingPlayerId);
            }}
          >View existing player</Button>
          {existingPlayer.isError && <p role="alert" className="text-sm text-danger-700 dark:text-danger-300">{getV2Error(existingPlayer.error).message}</p>}
        </div>
        : <Button className="mt-3 min-h-11" onClick={onSelect}>Select {player.name}</Button>}
    </article>
  );
};

const WyscoutPlayerDataPanel: React.FC<Props> = ({workspaceId, player, capabilities, selectedMatchId, onOpenFixture}) => {
  const queryClient = useQueryClient();
  const canImport = capabilities.includes('player.create');
  const canSyncSquad = capabilities.includes('wyscout.squad.sync');
  const linked = Boolean(player.wyscout_player_id && player.wyscout_player_id > 0);
  const canViewClubRoster = canImport && linked && player.scope === 'external';
  const currentRef = React.useRef({workspaceId, playerId: player.id, canImport, canSyncSquad, canViewClubRoster});
  const importPendingRef = React.useRef<object | null>(null);
  const syncPendingRef = React.useRef<object | null>(null);
  currentRef.current = {workspaceId, playerId: player.id, canImport, canSyncSquad, canViewClubRoster};
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState<{
    workspaceId: string;
    playerId: string;
    query: string;
  } | null>(null);
  const [searchMonths, setSearchMonths] = useState<WyscoutImportMonths>(12);
  const [importDialog, setImportDialog] = useState<ImportDialog>(null);
  const [syncResult, setSyncResult] = useState<SquadSyncResult | null>(null);
  const [squadInfoOpen, setSquadInfoOpen] = useState(false);
  const [clubRosterOpen, setClubRosterOpen] = useState(false);
  const [clubRosterResult, setClubRosterResult] = useState<WyscoutClubRosterImportResult | null>(null);
  const panelRef = useRef<HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const restoreSearchFocusRef = useRef(false);
  const searchFocusContextRef = useRef('');

  useEffect(() => {
    const trimmed = search.trim();
    if (Array.from(trimmed).length < 3) {
      setCommittedSearch(null);
      return;
    }
    const timer = window.setTimeout(() => setCommittedSearch({
      workspaceId,
      playerId: player.id,
      query: trimmed,
    }), 300);
    return () => window.clearTimeout(timer);
  }, [player.id, search, workspaceId]);

  const committedQuery = committedSearch?.workspaceId === workspaceId
    && committedSearch.playerId === player.id
    ? committedSearch.query
    : '';

  const searchQuery = useQuery({
    queryKey: ['wyscout-player-link-search', workspaceId, player.id, committedQuery, searchMonths],
    queryFn: ({signal}) => wyscoutImportAPI.search(workspaceId, committedQuery, searchMonths, signal),
    enabled: Boolean(!linked && canImport && searchOpen && workspaceId && committedQuery),
    retry: false,
  });

  const invalidatePlayerData = async (requestWorkspaceId: string, requestPlayerId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({queryKey: playerKeys.detail(requestWorkspaceId, requestPlayerId), exact: true}),
      queryClient.invalidateQueries({queryKey: playerKeys.wyscoutImport(requestWorkspaceId, requestPlayerId)}),
      queryClient.invalidateQueries({queryKey: [...playerKeys.detail(requestWorkspaceId, requestPlayerId), 'wyscout-analytics']}),
      queryClient.invalidateQueries({queryKey: playerKeys.all(requestWorkspaceId)}),
    ]);
  };

  const individualImport = useMutation({
    mutationFn: ({dialog, workspaceId: requestWorkspaceId, playerId: requestPlayerId}: ImportVariables) => {
      const current = currentRef.current;
      if (!current.canImport
        || current.workspaceId !== requestWorkspaceId
        || current.playerId !== requestPlayerId) return Promise.reject(new Error('Import is unavailable'));
      return dialog.kind === 'link'
        ? wyscoutImportAPI.link(requestWorkspaceId, requestPlayerId, dialog.player.wyscoutPlayerId, dialog.months)
        : wyscoutImportAPI.startLinked(requestWorkspaceId, requestPlayerId, dialog.months);
    },
    onSuccess: async (_data, variables) => {
      const current = currentRef.current;
      if (current.workspaceId === variables.workspaceId && current.playerId === variables.playerId) setImportDialog(null);
      await invalidatePlayerData(variables.workspaceId, variables.playerId);
    },
    onSettled: (_data, _error, variables) => {
      if (importPendingRef.current === variables.token) importPendingRef.current = null;
    },
  });

  const squadSync = useMutation({
    mutationFn: ({workspaceId: requestWorkspaceId, playerId: requestPlayerId}: SyncVariables) => {
      const current = currentRef.current;
      if (!current.canSyncSquad
        || current.workspaceId !== requestWorkspaceId
        || current.playerId !== requestPlayerId) return Promise.reject(new Error('Squad synchronization is unavailable'));
      return playersV2API.syncSquad(requestWorkspaceId);
    },
    onSuccess: async (result, variables) => {
      const current = currentRef.current;
      if (current.workspaceId === variables.workspaceId
        && current.playerId === variables.playerId
        && current.canSyncSquad) setSyncResult(result);
      await Promise.all([
        queryClient.invalidateQueries({queryKey: playerKeys.all(variables.workspaceId)}),
        queryClient.invalidateQueries({queryKey: playerKeys.latestSquadSync(variables.workspaceId)}),
        queryClient.invalidateQueries({queryKey: playerKeys.squadReview(variables.workspaceId)}),
        queryClient.invalidateQueries({queryKey: playerKeys.detail(variables.workspaceId, variables.playerId), exact: true}),
      ]);
    },
    onSettled: (_data, _error, variables) => {
      if (syncPendingRef.current === variables.token) syncPendingRef.current = null;
    },
  });
  const resetIndividualImportRef = React.useRef(individualImport.reset);
  const resetSquadSyncRef = React.useRef(squadSync.reset);
  resetIndividualImportRef.current = individualImport.reset;
  resetSquadSyncRef.current = squadSync.reset;

  const startIndividualImport = (dialog: Exclude<ImportDialog, null>): Promise<unknown> => {
    const current = currentRef.current;
    if (!current.canImport || importPendingRef.current) return Promise.reject(new Error('Import is unavailable'));
    const token = {};
    importPendingRef.current = token;
    return individualImport.mutateAsync({
      dialog,
      workspaceId: current.workspaceId,
      playerId: current.playerId,
      token,
    });
  };

  const startSquadSync = () => {
    const current = currentRef.current;
    if (!current.canSyncSquad || syncPendingRef.current) return;
    const token = {};
    syncPendingRef.current = token;
    setSyncResult(null);
    squadSync.mutate({workspaceId: current.workspaceId, playerId: current.playerId, token});
  };

  useEffect(() => {
    restoreSearchFocusRef.current = false;
    searchFocusContextRef.current = '';
    setSearchOpen(false);
    setSearch('');
    setCommittedSearch(null);
    setSearchMonths(12);
    setImportDialog(null);
    setSyncResult(null);
    setSquadInfoOpen(false);
    setClubRosterOpen(false);
    setClubRosterResult(null);
    importPendingRef.current = null;
    syncPendingRef.current = null;
    resetIndividualImportRef.current();
    resetSquadSyncRef.current();
  }, [linked, player.id, player.scope, workspaceId]);

  useEffect(() => {
    if (canImport) return;
    setSearchOpen(false);
    setSearch('');
    setCommittedSearch(null);
    setImportDialog(null);
    setClubRosterOpen(false);
    setClubRosterResult(null);
    importPendingRef.current = null;
    resetIndividualImportRef.current();
  }, [canImport]);

  useEffect(() => {
    if (canSyncSquad) return;
    setSyncResult(null);
    setSquadInfoOpen(false);
    syncPendingRef.current = null;
    resetSquadSyncRef.current();
  }, [canSyncSquad]);

  useEffect(() => {
    const context = `${workspaceId}:${player.id}`;
    if (searchOpen && searchFocusContextRef.current === context) {
      searchInputRef.current?.focus();
    } else if (!searchOpen && restoreSearchFocusRef.current) {
      restoreSearchFocusRef.current = false;
      panelRef.current?.querySelector<HTMLButtonElement>('[data-wyscout-search-opener]')?.focus();
    }
  }, [player.id, searchOpen, workspaceId]);

  const importError = individualImport.isError ? getV2Error(individualImport.error) : null;
  const conflict = routeablePlayerConflict(importError?.details);
  const conflictHref = conflict
    ? `${conflict.scope === 'squad' ? '/players' : '/scouting'}/${conflict.player_id}`
    : '';

  const dialogMessage = importDialog?.kind === 'link' && <div className="space-y-3">
    <p>Link <strong>{importDialog.player.name}</strong> to <strong>{player.name}</strong> and begin importing match history.</p>
    <p>Provider-owned profile details will refresh. Reports and staff-authored data will remain unchanged.</p>
    <PeriodSelect
      value={importDialog.months}
      disabled={individualImport.isPending}
      onChange={months => setImportDialog(current => current ? {...current, months} : null)}
    />
    {importError && <p role="alert" className="text-danger-700 dark:text-danger-300">
      {importError.message}
      {conflictHref && <Link className="ml-2 font-semibold underline" to={conflictHref}>View linked player</Link>}
    </p>}
  </div>;

  const squadInfo = <div className="space-y-4">
    <section>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">What it does</h3>
      <p className="mt-1">Reconciles the entire workspace squad with its configured WyScout team. It can add new active players, refresh provider-owned profile details, reactivate linked players, and send missing or inactive linked players for human review rather than deleting them.</p>
    </section>
    <section>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">When to use it</h3>
      <p className="mt-1">Use it after signings, departures, transfers, or suspected provider-profile changes, or when the current squad appears stale.</p>
    </section>
    <section>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">What it does not do</h3>
      <p className="mt-1">It does not import detailed match statistics for this player. It does not overwrite reports, notes, documents, assessments, medical availability, or other staff-authored records.</p>
    </section>
  </div>;

  const squadAction = canSyncSquad ? <article aria-labelledby="wyscout-squad-records-heading" className="min-w-0 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
    <div className="flex items-center justify-between gap-3">
      <h3 id="wyscout-squad-records-heading" className="font-semibold text-gray-900 dark:text-gray-100">Squad records</h3>
      <button type="button" aria-label="About syncing squad records" onClick={() => setSquadInfoOpen(true)} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:text-gray-300 dark:hover:bg-gray-800">
        <InformationCircleIcon aria-hidden="true" className="h-6 w-6" />
      </button>
    </div>
    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Reconcile the workspace's current squad and provider-owned profile details.</p>
    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Workspace-wide action</p>
    <Button variant="outline" className="mt-3 min-h-11 whitespace-normal" loading={squadSync.isPending} loadingLabel="Syncing squad records" onClick={startSquadSync}>Sync squad records</Button>
    {squadSync.isError && <p role="alert" className="mt-3 text-sm text-danger-700 dark:text-danger-300">{getV2Error(squadSync.error).message}</p>}
    {syncResult && <div className="mt-3 text-sm text-gray-700 dark:text-gray-200">
      <p role="status" aria-label="Squad synchronization result" aria-live="polite">{syncResultLabel(syncResult)}</p>
      {syncResult.review > 0 && <Link to="/players?review=wyscout" className="mt-2 inline-flex min-h-11 items-center font-semibold text-club-blue-700 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:text-club-blue-300">Review players</Link>}
    </div>}
  </article> : null;

  const clubRosterAction = canViewClubRoster ? <article aria-labelledby="wyscout-club-roster-heading" className="min-w-0 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
    <h3 id="wyscout-club-roster-heading" className="font-semibold text-gray-900 dark:text-gray-100">Club roster</h3>
    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Choose external scouting profiles from this player's current provider club and queue their statistics imports.</p>
    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Selected scouting profiles</p>
    <Button variant="outline" className="mt-3 min-h-11" onClick={() => {
      setClubRosterResult(null);
      setClubRosterOpen(true);
    }}>View club roster</Button>
    {clubRosterResult && <div role="status" aria-label="Club roster import result" aria-live="polite" className="mt-3 text-sm text-gray-700 dark:text-gray-200">
      <p>{clubRosterResult.created} created, {clubRosterResult.refreshed} refreshed, {clubRosterResult.unchanged} unchanged.</p>
      <p>{clubRosterResult.queued} statistics imports queued, {clubRosterResult.reused} active imports reused.</p>
      <Link to="/scouting" className="mt-2 inline-flex min-h-11 items-center font-semibold underline">View Scouting</Link>
    </div>}
  </article> : null;

  return (
    <section ref={panelRef} aria-labelledby="wyscout-player-data-heading" className="min-w-0 space-y-5">
      {!linked && <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 id="wyscout-player-data-heading" className="text-lg font-bold text-gray-900 dark:text-gray-100">WyScout data</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Link and refresh provider match data without changing staff-authored player records.</p>
          </div>
        </div>
        {squadAction && <div className="mt-5">{squadAction}</div>}

        {!canImport && <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">WyScout analytics are unavailable until this player is linked by an authorized staff member.</p>}
        {canImport && !searchOpen && <Button data-wyscout-search-opener className="mt-4 min-h-11" onClick={() => {
          restoreSearchFocusRef.current = true;
          searchFocusContextRef.current = `${workspaceId}:${player.id}`;
          setSearchOpen(true);
        }}>Find on WyScout</Button>}

        {canImport && searchOpen && <div className="mt-5 space-y-4 border-t border-gray-200 pt-5 dark:border-gray-700">
          <div role="search" aria-label="WyScout player search" className="grid gap-4 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
            <PeriodSelect value={searchMonths} onChange={setSearchMonths} />
            <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200">
              Search WyScout players
              <input ref={searchInputRef} type="search" value={search} onChange={event => setSearch(event.target.value)} autoComplete="off" placeholder="Player name" className="mt-1 block min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-gray-900 placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100" />
            </label>
          </div>
          <Button variant="outline" onClick={() => setSearchOpen(false)}>Close WyScout search</Button>
          {Array.from(search.trim()).length < 3 && <p className="text-sm text-gray-600 dark:text-gray-300">Enter at least 3 characters to search.</p>}
          {searchQuery.isFetching && !searchQuery.data && <LoadingStatus label="Searching WyScout players..." />}
          {searchQuery.isError && <div role="alert" className="text-sm text-danger-700 dark:text-danger-300">{getV2Error(searchQuery.error).message} <button type="button" className="min-h-11 font-semibold underline" onClick={() => { void searchQuery.refetch(); }}>Try search again</button></div>}
          {searchQuery.data?.warnings.map((warning: string) => <p key={warning} role="alert" className="text-sm text-amber-800 dark:text-amber-200">{warning}</p>)}
          {searchQuery.data && searchQuery.data.data.length === 0 && <p className="text-sm text-gray-600 dark:text-gray-300">No WyScout players matched your search.</p>}
          {searchQuery.data && searchQuery.data.data.length > 0 && <div className="grid gap-3 lg:grid-cols-2">
            {searchQuery.data.data.map((result: WyscoutPlayerSearchResult) => <SearchResult key={result.wyscoutPlayerId} player={result} currentPlayerId={player.id} workspaceId={workspaceId} onSelect={() => setImportDialog({kind: 'link', player: result, months: searchMonths})} />)}
          </div>}
        </div>}
      </div>}

      {linked && <WyscoutImportPanel
        workspaceId={workspaceId}
        playerId={player.id}
        playerName={player.name}
        position={player.position}
        canImport={canImport}
        squadAction={squadAction}
        clubRosterAction={clubRosterAction}
        selectedMatchId={selectedMatchId}
        onOpenFixture={onOpenFixture}
        onStartImport={months => {
          const request = {kind: 'linked' as const, months};
          setImportDialog(request);
          return startIndividualImport(request);
        }}
      />}

      <WyscoutClubRosterDialog
        open={clubRosterOpen && canViewClubRoster}
        workspaceId={workspaceId}
        playerId={player.id}
        onClose={() => setClubRosterOpen(false)}
        onImported={(result, context) => {
          void Promise.all([
            queryClient.invalidateQueries({queryKey: [...playerKeys.all(context.workspaceId), 'squad']}),
            queryClient.invalidateQueries({queryKey: [...playerKeys.all(context.workspaceId), 'external']}),
            queryClient.invalidateQueries({queryKey: playerKeys.detail(context.workspaceId, context.playerId), exact: true}),
            ...context.existingPlayerIds.map(existingPlayerId => queryClient.invalidateQueries({
              queryKey: playerKeys.detail(context.workspaceId, existingPlayerId),
              exact: true,
            })),
            queryClient.invalidateQueries({queryKey: playerKeys.wyscoutClubRoster(context.workspaceId, context.playerId), exact: true}),
          ]);
          const current = currentRef.current;
          if (current.canViewClubRoster
            && current.workspaceId === context.workspaceId
            && current.playerId === context.playerId) {
            setClubRosterOpen(false);
            setClubRosterResult(result);
          }
        }}
      />

      <ConfirmModal
        isOpen={squadInfoOpen}
        title="About syncing squad records"
        message={squadInfo}
        confirmLabel="Close"
        hideCancel
        onClose={() => setSquadInfoOpen(false)}
        onConfirm={() => setSquadInfoOpen(false)}
      />

      <ConfirmModal
        isOpen={importDialog?.kind === 'link'}
        title="Link WyScout player"
        message={dialogMessage}
        confirmLabel="Link and import"
        loading={individualImport.isPending}
        onClose={() => {
          if (!individualImport.isPending) {
            individualImport.reset();
            setImportDialog(null);
          }
        }}
        onConfirm={() => {
          if (!importDialog) return;
          void startIndividualImport(importDialog).catch(() => {});
        }}
      />
    </section>
  );
};

export default WyscoutPlayerDataPanel;
